import React from 'react'
import {localizeDelta, calcGlobalPos, calculateAngle, subAngle, calculateAB} from './utils'
import SideControl from './SideControl'
import ResizeControl from './ResizeControl'

const RotatableBox = ({
  children,
  onResizeEnd,
  onRotateEnd,
  onClickDelete,
  onMoveEnd,
  angle: pAngle,
  dim: pDim,
  pos,
  showControls,
  lockAspectRatio,
}) => {
  const [angle, setAngle] = React.useState(pAngle)
  const [movPos, setMovPos] = React.useState({x: 0, y: 0})
  const [action, setAction] = React.useState({type: null, mod: null}) // rotate | resize
  const [mosOrigin, setMosOrigin] = React.useState({x: 0, y: 0})
  const [angleOrigin, setAngleOrigin] = React.useState({degrees: 0, radians: 0})
  const [resizeData, setResizeData] = React.useState({offsetX: 0, offsetY: 0, width: 0, height: 0})
  const refEditBox = React.useRef(null)

  const getRelativeCoordinates = event => {
    const clientRect = refEditBox.current.getBoundingClientRect()
    return {
      mousex: event.pageX - clientRect.left - clientRect.width / 2,
      mousey: event.pageY - clientRect.top - clientRect.height / 2,
    }
  }

  const handleMouseMove = React.useCallback(
    event => {
      const ratioMultiplierX = pDim.width / pDim.height
      const ratioMultiplierY = pDim.height / pDim.width
      event.preventDefault()
      if (action.type === 'rotate') {
        const angle = calculateAngle(getRelativeCoordinates(event))
        const rAngle = subAngle(angle, angleOrigin)
        const nAngle = subAngle({radians: rAngle.radians}, pAngle)
        refEditBox.current.style.transform = `rotateZ(${rAngle.degrees}deg)`

        setAngle(nAngle)
      } else if (action.type === 'move') {
        const delta_x_global = event.pageX - mosOrigin.x
        const delta_y_global = event.pageY - mosOrigin.y
        const localDelta = localizeDelta({x: delta_x_global, y: delta_y_global}, -pAngle.radians)
        refEditBox.current.style.top = `${localDelta.y}px`
        refEditBox.current.style.left = `${localDelta.x}px`
        setMovPos({x: delta_x_global, y: delta_y_global})
      } else if (action.type === 'resize') {
        // do the resize logic here
        const delta_x_global = event.pageX - mosOrigin.x
        const delta_y_global = event.pageY - mosOrigin.y

        const localDelta = localizeDelta({x: delta_x_global, y: delta_y_global}, -pAngle.radians)

        let offsetX = 0
        let offsetY = 0
        let nWidth = pDim.width
        let nHeight = pDim.height
        let globalDelta = {x: 0, y: 0}

        if (action.mod === 'rs_bot') {
          nHeight += localDelta.y

          if (lockAspectRatio) {
            nWidth = nHeight * ratioMultiplierX
          }
        }
        if (action.mod === 'rs_right') {
          nWidth += localDelta.x
          if (lockAspectRatio) {
            nHeight = nWidth * ratioMultiplierY
          }
        }
        if (action.mod === 'rs_top') {
          offsetY = localDelta.y
          nHeight -= localDelta.y
          globalDelta = calculateAB(localizeDelta({x: 0, y: offsetY}, pAngle.radians))
          if (lockAspectRatio) {
            nWidth = nHeight * ratioMultiplierX
          }
        }
        if (action.mod === 'rs_left') {
          offsetX = localDelta.x
          nWidth -= localDelta.x
          globalDelta = calculateAB(localizeDelta({x: offsetX, y: 0}, pAngle.radians))
          if (lockAspectRatio) {
            nHeight = nWidth * ratioMultiplierY
          }
        }
        if (action.mod === 'rs_topright') {
          if (lockAspectRatio) {
            nHeight -= localDelta.y
            nWidth = nHeight * ratioMultiplierX
            offsetY = localDelta.y
          } else {
            offsetY = localDelta.y
            nHeight -= localDelta.y
            nWidth += localDelta.x
          }
          globalDelta = calculateAB(localizeDelta({x: 0, y: offsetY}, pAngle.radians))
        }
        if (action.mod === 'rs_topleft') {
          if (lockAspectRatio) {
            nHeight -= localDelta.y
            nWidth = nHeight * ratioMultiplierX
            offsetY = localDelta.y
            offsetX = pDim.width - nWidth
          } else {
            nHeight -= localDelta.y
            nWidth -= localDelta.x
            offsetY = localDelta.y
            offsetX = localDelta.x
          }
          globalDelta = calculateAB(localizeDelta({x: offsetX, y: offsetY}, pAngle.radians))
        }
        if (action.mod === 'rs_bottomright') {
          if (lockAspectRatio) {
            nHeight += localDelta.y
            nWidth = nHeight * ratioMultiplierX
          } else {
            nHeight += localDelta.y
            nWidth += localDelta.x
          }
        }
        if (action.mod === 'rs_bottomleft') {
          if (lockAspectRatio) {
            nHeight += localDelta.y
            nWidth = nHeight * ratioMultiplierX
            offsetX = pDim.width - nWidth
          } else {
            nHeight += localDelta.y
            offsetX = localDelta.x
            nWidth -= localDelta.x
          }
          globalDelta = calculateAB(localizeDelta({x: offsetX, y: 0}, pAngle.radians))
        }

        refEditBox.current.style.left = `${offsetX}px`
        refEditBox.current.style.top = `${offsetY}px`
        refEditBox.current.style.height = `${nHeight}px`
        refEditBox.current.style.width = `${nWidth}px`

        // calculate rotated left and top
        const a = calcGlobalPos(pDim, pAngle.radians, {
          top: pos.top, // unrotated top
          left: pos.left, // unrotated left
        })
        const b = calcGlobalPos({width: nWidth, height: nHeight}, pAngle.radians, {
          top: pos.top,
          left: pos.left,
        })
        offsetX = globalDelta.x + (a.left - b.left)
        offsetY = globalDelta.y + (a.top - b.top)
        setResizeData({offsetX, offsetY, width: nWidth, height: nHeight})
      }
    },
    [
      action,
      setAngle,
      pAngle,
      mosOrigin,
      pDim,
      setResizeData,
      pos,
      setMovPos,
      angleOrigin,
      lockAspectRatio,
    ]
  )

  const handleMouseUp = React.useCallback(
    event => {
      event.preventDefault()
      event.stopPropagation()
      // event.cancel = true;
      // if (event.stopImmediatePropagation){
      //   event.stopImmediatePropagation()
      // }
      if (action.type === 'rotate') {
        onRotateEnd(angle)
        refEditBox.current.style.transform = `rotateZ(0deg)`
      } else if (action.type === 'move') {
        onMoveEnd(movPos)
        refEditBox.current.style.top = `0`
        refEditBox.current.style.left = `0`
      } else if (action.type === 'resize') {
        onResizeEnd(resizeData)
        refEditBox.current.style.width = `100%`
        refEditBox.current.style.height = `100%`
        refEditBox.current.style.top = `0`
        refEditBox.current.style.left = `0`
      }
      setAction({type: null, mod: ''})
    },
    [setAction, onRotateEnd, onResizeEnd, onMoveEnd, resizeData, action, angle, movPos]
  )

  const rotateControlMouseDown = event => {
    event.preventDefault()
    event.stopPropagation()
    // event.cancel = true;
    // if (event.stopImmediatePropagation){
    //   event.stopImmediatePropagation()
    // }
    document.activeElement.blur()
    const angle = calculateAngle(getRelativeCoordinates(event))

    setAngleOrigin(angle)
    setAction({type: 'rotate', mod: null})
  }

  const moveControlMouseDown = event => {
    event.preventDefault()
    event.stopPropagation()
    // event.cancel = true;
    // if (event.stopImmediatePropagation){
    //   event.stopImmediatePropagation()
    // }
    document.activeElement.blur()
    setMosOrigin({x: event.pageX, y: event.pageY})
    setAction({type: 'move', mod: null})
  }

  const resizeControlMouseDown = (event, modifier = null) => {
    event.preventDefault()
    event.stopPropagation()
    // event.cancel = true;
    // if (event.stopImmediatePropagation){
    //   event.stopImmediatePropagation()
    // }
    document.activeElement.blur()
    setMosOrigin({x: event.pageX, y: event.pageY})
    setAction({type: 'resize', mod: modifier})
  }

  React.useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)
    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
    }
  }, [handleMouseMove, handleMouseUp])

  return (
    <div
      ref={refEditBox}
      className="ap-barcode-rrd__rotatable-box"
      style={
        showControls
          ? null
          : {
              border: 'none',
            }
      }
    >
      {children}

      {showControls && (
        <SideControl
          onMouseDownRotate={rotateControlMouseDown}
          onMouseDownMove={moveControlMouseDown}
          onClickDelete={onClickDelete}
        />
      )}
      {showControls && <ResizeControl onMouseDown={resizeControlMouseDown} />}
    </div>
  )
}

export default RotatableBox
